import React, { useEffect, useState } from "react";
import { Col, Spinner } from "reactstrap";
import {
  congRoles,
  BEFORE_TWENTIETH,
  TWENTIETH_CONGRESS,
  ALL_OPTION,
} from "utility/constants";
import { Toast } from "utility/helpers";
import { updateDraftNews } from "services/services.news";
import { useLocation, useNavigate } from "react-router";
import { genericGet, genericPost } from "services/services.general";
import { fetchAllRegions } from "services/services.congressman";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import { AddButton, HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import {
  allRegion,
  customRegion,
  isNotAllOrCustom,
} from "views/events/constants";
import NewsFormSection from "./NewsFormSection";

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: 4rem;
`;

const StyledSaveButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const StyledCancelButton = styled(AddButton)`
  border-color: ${({ theme }) => theme.colors.buttonFormRed};
  width: 130px;
  margin-right: 1rem;
  color: ${({ theme }) => theme.colors.buttonFormRed};
  background: white;
`;

const HeaderTextSection = styled.div`
  margin-top: 24px;
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const officeOptions = [
  {
    value: 1,
    label: "Congressional Office",
  },
  {
    value: 2,
    label: "National Agencies",
  },
];

const AddAnnouncementForm = () => {
  const location = useLocation();
  const { user } = useAuth();
  const row = location.state?.row || null;
  const { action } = location.state;
  const role = user?.type;
  const showForms = !congRoles.includes(role);

  const [form, setForm] = useState({
    title: "",
    description: "",
    message: "",
    sms: true,
    email: false,
    cos: false,
    editor: false,
    director: false,
  });

  const [recipients, setRecipients] = useState({
    include: [],
    exclude: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);

  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const [isLegacyData, setIsLegacyData] = useState(false);

  const navigate = useNavigate();

  const resetForm = () => {
    setRecipients({
      include: [],
      exclude: [],
    });

    setForm({
      title: "",
      description: "",
      message: "",
      sms: false,
      email: false,
      cos: false,
      editor: false,
      director: false,
    });

    setUploadedFiles([]);
    setSelectedOffice(null);
    setSelectedRegion(null);
    setSelectedRecipients([]);
    setSelectedAgencies([]);
  };

  const sendData = (formData) => {
    genericPost({
      url: "/news",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          navigate("/news");

          Toast({
            type: 1,
            content: "Successfully created news.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const sendEmail = (formData) => {
    genericPost({
      url: "/news/send-email",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (!isSuccess) {
          Toast({
            type: 4,
            content: "An error occurred when sending emails.",
          });
        }
      },
    });
  };

  const sendFinalDraft = (formData) => {
    updateDraftNews({
      param: row.id,
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          resetForm();
          navigate("/news");

          Toast({
            type: 1,
            content: "Successfully edited news.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const validate = () => {
    if (!form.title) {
      return { isValid: false, message: "News title cannot be empty" };
    }

    if (!form.message) {
      return { isValid: false, message: "News message cannot be empty" };
    }

    if (recipients.include.length === 0) {
      return { isValid: false, message: "Recipient cannot be empty" };
    }

    if (!selectedOffice) {
      return { isValid: false, message: "Office cannot be empty" };
    }

    if (!selectedRegion) {
      return { isValid: false, message: "Region cannot be empty" };
    }

    return { isValid: true, message: "" };
  };

  const regions = regionOptions?.filter(isNotAllOrCustom) || [];

  const getRegionId = () => {
    if (selectedRegion.some((region) => region.value === 0)) return null;

    if (selectedRegion.some((region) => region.value === ALL_OPTION)) {
      return regions.map((op) => op.value).join(",");
    }

    return selectedRegion?.map((region) => region.value).join(",");
  };

  const handleSendOnClicked = () => {
    const validation = validate();

    if (validation.isValid) {
      setIsLoading(true);

      const formData = {
        title: form.title,
        description: form.description,
        attachedFileURL: uploadedFiles?.map((file) => file.location),
        excludedRecipients: "",
        regionId: getRegionId(),
        includeEditor: form.editor ? 1 : 0,
        includeDirector: form.director ? 1 : 0,
      };

      const emailForm = {
        title: form.title,
        message: form.message,
        includeCOS: form.cos ? 1 : 0,
        includeEditor: form.editor ? 1 : 0,
        includeDirector: form.director ? 1 : 0,
      };

      if (!row) {
        formData.message = form.message;
        formData.sendWithSMS = form.sms;
        formData.sendWithEmail = form.email;
        formData.includeCOS = form.cos;
        formData.recipients = recipients.include.map((id) => id).join(";");
        formData.office = selectedOffice.label;
        formData.agencies = selectedAgencies.map((agency) => agency.value);

        emailForm.recipients = recipients.include.map((id) => id).join(";");

        sendData(formData);
        if (form.email) sendEmail(emailForm);
      } else {
        formData.id = row.id;
        sendFinalDraft(formData);
      }
    } else {
      Toast({
        type: 4,
        content: validation.message,
      });
    }
  };

  const fetchAgencies = () => {
    genericGet({
      url: "/national-agency",
      callback: (data) => {
        const options = data.rows.map(({ agencyAcronym, id }) => ({
          label: agencyAcronym,
          value: id,
        }));

        setAgencyOptions(options);
      },
    });
  };

  const fetchRegions = () => {
    fetchAllRegions({
      params: {
        includeCentral: true,
      },
      callback: (data) => {
        if (data.length) {
          let options = data.map(({ acronym, id, omegaId }) => ({
            omegaId,
            label: acronym,
            value: id,
          }));

          options = options.filter((op) => op.value !== 18);
          options.unshift(allRegion);

          setRegionOptions(options);
        }
      },
    });
  };

  const setFromData = () => {
    if (new Date(row.createdAt) < BEFORE_TWENTIETH) setIsLegacyData(true);

    setForm({
      title: row.title,
      description: row.description,
      message: row.message,
      cos: row.includeCOS,
      sms: row.sendWithSMS,
      email: row.sendWithEmail,
      editor: row.includeEditor,
      director: row.includeDirector,
    });

    const files = [];

    if (row?.attachedFileURL?.length !== 0) {
      row?.attachedFileURL?.forEach((fileURL) => {
        const fileArray = fileURL.split("/");

        const file = {
          name: decodeURIComponent(fileArray[fileArray.length - 1]),
          location: fileURL,
          percent: 100,
        };

        files.push(file);
      });
    }

    setUploadedFiles(files);
    const office = officeOptions.find((off) => off.label === row.office);

    if (office) setSelectedOffice(office);
    else {
      setSelectedOffice(officeOptions[0]);
    }
  };

  const fetchAllCongressmen = () => {
    const regionParams = selectedRegion
      ?.map((region) => region.value)
      .join(",");

    const processData = (rows) => {
      const newData = rows.map(({ id, fullName, District, contact }) => ({
        name: fullName,
        id,
        designation: "",
        agency: "",
        area: District?.Region?.acronym || "",
        contact,
      }));

      setSelectedRecipients(newData);

      const included = row?.recipients?.split(";") || [];

      const includedIds = rows
        .filter(
          ({ id }) => included.length === 0 || included.includes(id.toString())
        )
        .map(({ id }) => id);

      setRecipients((prev) => ({
        ...prev,
        include: includedIds,
      }));
    };

    const params = {
      region: regionParams,
      pagination: false,
      ...(isLegacyData ? {} : { congress: TWENTIETH_CONGRESS }),
    };

    genericGet({
      url: "congressman/lookup/regions",
      params,
      callback: (data) => {
        const rows = data?.rows || [];

        if (rows.length === 0) {
          setSelectedRecipients([]);

          setRecipients((prev) => ({
            ...prev,
            include: [],
          }));

          return;
        }

        processData(rows);
      },
    });
  };

  const fetchNationalAgencies = () => {
    const agencies = selectedAgencies?.map((agency) => agency.value).join(",");

    const regs = selectedRegion?.some((region) => region.value === ALL_OPTION)
      ? null
      : selectedRegion?.map((region) => region.value).join(",");

    genericGet({
      url: "/national-agency-user",
      params: {
        agency: agencies,
        region: regs,
        pagination: false,
      },
      callback: (data) => {
        if (data.rows.length !== 0) {
          const newData = data.rows.map(
            ({ id, area, name, designation, contact, NationalAgency }) => ({
              name,
              id,
              designation,
              contact,
              agency: NationalAgency.agencyAcronym,
              area,
            })
          );

          setSelectedRecipients(newData);

          const included = row?.recipients?.split(";");

          const includedIds = !included
            ? data.rows.map(({ id }) => id)
            : data.rows
                .filter(({ id }) => included.includes(id.toString()))
                .map(({ id }) => id);

          setRecipients({
            ...recipients,
            include: includedIds,
          });
        } else {
          setSelectedRecipients([]);

          setRecipients({
            ...recipients,
            include: [],
          });
        }
      },
    });
  };

  const setSelectedCustomRecipients = () => {
    setIsLoading(true);

    let included = row?.recipients?.split(";") || [];
    included = [...new Set(included.map((id) => Number(id)))];
    const includedSet = new Set(included);

    const mapRecipients = (data = []) =>
      data
        .filter((item) => includedSet.has(item.id))
        .map((item) => ({
          name: item.name,
          id: item.id,
          designation: "Congressman",
          contact: item.contact,
          agency: "",
          area: item?.region?.acronym ?? "",
          cos: item.cos?.name,
          cosContact: item.cos?.contact,
        }));

    const selecRecipients = [];

    const params = !isLegacyData ? { congress: TWENTIETH_CONGRESS } : undefined;

    genericGet({
      url: "/congressman",
      params,
      callback: (data = []) => {
        selecRecipients.push(...mapRecipients(data));

        setSelectedRecipients(selecRecipients);

        setRecipients({
          ...recipients,
          include: included,
        });

        setIsLoading(false);
      },
    });
  };

  const getHeaderText = () => {
    if (action === EDIT_ACTION) return "Edit Post";
    if (action === VIEW_ACTION) return "Post Details";

    return "Create a New Post";
  };

  const getFilteredRegionOptions = () => {
    const custom = regionOptions.filter((op) => op.value === 0);

    const all = selectedRegion?.length
      ? selectedRegion?.some((op) => op?.value === ALL_OPTION)
      : false;

    if (selectedOffice?.value === 1) {
      return all ? custom : regionOptions;
    }

    return all ? [] : regionOptions;
  };

  const handleRegionOnChange = (option) => {
    if (option?.some((op) => op.value === 0)) {
      setSelectedRegion([customRegion]);
      setIsCustomSelected(true);

      return;
    }

    if (option.length > 1 && option?.some((op) => op.value === ALL_OPTION)) {
      setSelectedRegion([allRegion]);

      return;
    }

    if (option.length !== 0 && option.length === regions.length) {
      setSelectedRegion([allRegion]);

      return;
    }

    setIsCustomSelected(false);
    setSelectedRegion(option);
  };

  useEffect(() => {
    if (row && regionOptions.length) {
      if (row.regionId) {
        const regs = row.regionId?.split(",");

        const region = regionOptions.filter((reg) =>
          regs.includes(reg.value.toString())
        );

        if (regions.length === region.length) setSelectedRegion([allRegion]);
        else setSelectedRegion(region);
      } else {
        setSelectedRegion([customRegion]);
        setSelectedCustomRecipients();
      }
    }
  }, [row, regionOptions]);

  useEffect(() => {
    if (row && agencyOptions.length) {
      if (row?.agencies && row?.agencies?.length) {
        const agency = agencyOptions.filter((agen) =>
          row.agencies?.includes(agen.value)
        );

        if (agency) setSelectedAgencies(agency);
      }
    }
  }, [row, agencyOptions]);

  useEffect(() => {
    if (row) {
      setFromData();
    }
  }, [row]);

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (selectedOffice?.value === 2 && agencyOptions.length === 0) {
      fetchAgencies();
    }
  }, [selectedOffice, agencyOptions]);

  useEffect(() => {
    if (selectedOffice) {
      setSelectedAgencies([]);
      setSelectedRecipients([]);
      setSelectedRegion(null);

      if (selectedOffice?.value === 1) {
        const options = regionOptions;

        if (!options.find((op) => op.value === 0)) {
          options.splice(1, 0, customRegion);
        }
      } else {
        const newOptions = regionOptions.filter((op) => op.value !== 0);
        setRegionOptions(newOptions);
      }
    }
  }, [selectedOffice]);

  useEffect(() => {
    if (selectedRegion) {
      if (selectedOffice?.value === 1 && selectedRegion.length) {
        fetchAllCongressmen();
      } else if (selectedOffice?.value === 2 && selectedRegion.length) {
        fetchNationalAgencies();
      } else {
        setSelectedRecipients([]);

        setRecipients({
          ...recipients,
          include: [],
        });
      }
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (selectedAgencies && selectedRegion && selectedOffice?.value === 2) {
      fetchNationalAgencies();
    }
  }, [selectedAgencies]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Wrapper>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getHeaderText()}</HeaderText>
        </HeaderTextSection>

        <NewsFormSection
          showForms={showForms}
          officeOptions={officeOptions}
          setSelectedOffice={setSelectedOffice}
          selectedOffice={selectedOffice}
          action={action}
          getFilteredRegionOptions={getFilteredRegionOptions}
          handleRegionOnChange={handleRegionOnChange}
          selectedRegion={selectedRegion}
          form={form}
          setForm={setForm}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          setSelectedAgencies={setSelectedAgencies}
          agencyOptions={agencyOptions}
          selectedAgencies={selectedAgencies}
          selectedRecipients={selectedRecipients}
          setRecipients={setRecipients}
          recipients={recipients}
          isCustomSelected={isCustomSelected}
        />

        <ButtonsContainer>
          <StyledCancelButton
            outline
            disabled={isLoading}
            onClick={() => navigate("/news")}
          >
            {"Cancel"}
          </StyledCancelButton>

          <StyledSaveButton
            onClick={() => handleSendOnClicked()}
            disabled={isLoading || action === VIEW_ACTION}
          >
            {isLoading ? (
              <Spinner
                animation="border"
                color="light"
                size="sm"
              />
            ) : (
              "Send"
            )}
          </StyledSaveButton>
        </ButtonsContainer>
      </Col>
    </Wrapper>
  );
};

export default AddAnnouncementForm;
